import React, { useState, useEffect, useMemo } from 'react';
import {
    Search,
    Coins,
    ExternalLink,
    Activity,
    Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { bondsAPI, custodianAPI, formatCurrency, formatDate, formatCompactCurrency } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';

const CustodianBondHoldings = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;

    // Get current custodian ID
    const currentCustodianId = user?._id || user?.id;

    // Fetch holdings from the holdings table
    const { data: holdingsData = [], isLoading: holdingsLoading, error: holdingsError } = useQuery({
        queryKey: ['custodian-holdings', user?.id, statusFilter],
        queryFn: () => custodianAPI.getHoldingsAsCustodian({ status: statusFilter === 'all' ? 'all' : 'active' }),
        enabled: !!user?.id,
        select: (resp) => {
            if (!resp) return [];
            const root = resp.data || resp;
            if (Array.isArray(root?.data)) return root.data;
            if (Array.isArray(root)) return root;
            return [];
        }
    });

    // Aggregate holdings by bond
    const bondHoldings = useMemo(() => {
        if (!holdingsData || holdingsData.length === 0) return [];

        // Group holdings by bondId
        const bondMap = new Map();

        holdingsData.forEach(holding => {
            const bondId = holding.bondId?._id || holding.bondId;
            const bondSnapshot = holding.bondSnapshot || {};

            if (!bondMap.has(bondId)) {
                bondMap.set(bondId, {
                    _id: bondId,
                    bondId: bondId,
                    bondName: bondSnapshot.bondName || 'Unknown Bond',
                    bondNumber: bondSnapshot.bondNumber,
                    isin: bondSnapshot.isin,
                    stockCode: bondSnapshot.stockCode,
                    productType: bondSnapshot.productType || 'Fixed Notes',
                    status: 'Active', // Holdings are for active bonds
                    createdAt: bondSnapshot.issueDate,
                    submittedAt: bondSnapshot.issueDate,
                    tokenDetails: {
                        symbol: bondSnapshot.stockCode,
                        tokenAddress: bondSnapshot.tokenAddress,
                        contractAddress: bondSnapshot.tokenAddress
                    },
                    allocationInfo: {
                        totalAllocationAmount: 0,
                        allocationsCount: 0
                    },
                    tokenHolders: 0,
                    holdings: [],
                    uniqueHolders: new Set() // Track unique holder IDs
                });
            }

            // Skip inactive holdings (zero remaining tokens or holdingsEndDate set)
            if ((holding.remainingTokenAmount || 0) <= 0 || holding.holdingsEndDate) {
                return; // Skip this holding
            }

            const bond = bondMap.get(bondId);
            bond.holdings.push(holding);
            bond.allocationInfo.totalAllocationAmount += holding.remainingTokenAmount || 0;
            bond.allocationInfo.allocationsCount += 1;

            // Add holder to unique holders set - include custodian itself and its sub-investors
            // API already filters by custodian, so all holdings here belong to this custodian
            const holderSnapshot = holding.holderSnapshot || {};
            const holderId = holding.holderId?._id || holding.holderId;
            
            // Exclude only holdings where the holder is a Primary Subscriber (PS direct holdings)
            // Note: primarySubscriberId field is just for tracking, not for filtering
            const isPrimarySubscriberHolder = holderSnapshot.isPrimarySubscriber === true;
            
            if (!isPrimarySubscriberHolder && holderId) {
                // Count both custodian itself and sub-investors (but not Primary Subscribers as holders)
                bond.uniqueHolders.add(String(holderId));
            }
        });

        // Convert to array and set tokenHolders count
        return Array.from(bondMap.values()).map(bond => {
            bond.tokenHolders = bond.uniqueHolders.size;
            delete bond.uniqueHolders; // Remove the Set before returning
            return bond;
        });
    }, [holdingsData]);

    // Percentage & approval state fetch removed per latest requirement.

    const getStatusBadge = (status) => {
        const statusClasses = {
            'Active': 'bg-green-100 text-green-800',
            'Listed': 'bg-green-100 text-green-800',
            'Pending Review': 'bg-yellow-100 text-yellow-800',
            'Approved': 'bg-blue-100 text-blue-800',
            'Rejected': 'bg-red-100 text-red-800',
            'Draft': 'bg-gray-100 text-gray-800',
            'Redemption in Process': 'bg-orange-100 text-orange-800',
            'Redemption Completed': 'bg-green-100 text-green-800',
            'Burn in Process': 'bg-red-100 text-red-800',
            'Burn Completed': 'bg-gray-100 text-gray-800',
            'Matured': 'bg-purple-100 text-purple-800',
            'Custodian Allocation Finalized': 'bg-green-100 text-green-800',
        };

        const statusIcons = {
            'Active': '✅',
            'Listed': '🏛️',
            'Pending Review': '⏳',
            'Approved': '✅',
            'Rejected': '❌',
            'Draft': '📝',
            'Redemption in Process': '🔄',
            'Redemption Completed': '✅',
            'Burn in Process': '🔥',
            'Burn Completed': '⚫',
            'Matured': '🏁',
            'Custodian Allocation Finalized': '✅',
        };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                <span className="mr-1">{statusIcons[status]}</span>
                {status}
            </span>
        );
    };

    // Removed allocation percentage logic per requirement; only displaying allocation amount now.

    // Filter bonds (search + type) - status filtering is done at API level
    const filteredBonds = bondHoldings
        .filter(bond => {
            const q = searchTerm.toLowerCase();
            if (!q) return true;
            return [bond.bondName, bond.isin, bond.productType, bond.status, bond.tokenDetails?.symbol]
                .filter(Boolean)
                .some(v => String(v).toLowerCase().includes(q));
        })
        .filter(bond => (typeFilter === 'all' || bond.productType === typeFilter));

    // Portfolio summary focused on allocation metrics
    const totalAllocatedValue = filteredBonds.reduce((sum, bond) => sum + (bond.allocationInfo?.totalAllocationAmount || 0), 0);
    const activeBonds = filteredBonds.filter(bond => ['Active', 'Listed'].includes(bond.status)).length;

    // Pagination (client-side since backend endpoint returns allocated list without pagination currently)
    const totalBonds = filteredBonds.length;
    const totalPages = Math.max(1, Math.ceil(totalBonds / PAGE_SIZE));
    // Reset page when filters or search change
    useEffect(() => { setCurrentPage(1); }, [searchTerm, typeFilter]);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const pageBonds = filteredBonds.slice(startIndex, endIndex);

    if (holdingsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holdings..." />;
    }

    if (holdingsError) {
        return (
            <div className="py-12 text-center">
                <p className="text-error-600">Error loading bond holdings: {holdingsError.message || 'Please try again.'}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 mt-4 text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
                {/* Page Header */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Bond Holdings</h1>
                        <p className="text-sm text-gray-500">
                            Track and manage allocated bond holdings under custodian management
                        </p>
                    </div>
                    {/* Hidden: View Allocations button */}
                    {/* <button
                        onClick={() => navigate('/custodian/allocation-preview')}
                        className="flex items-center space-x-2 btn-primary"
                    >
                        <Activity className="w-4 h-4" />
                        <span>View Allocations</span>
                    </button> */}
                </div>

                {/* Summary widgets removed per request */}

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                    {/* Search Card - 60% width (3 out of 5 columns) */}
                    <div className="card lg:col-span-3">
                        <div className="card-body">
                            <div className="relative">
                                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                                <input
                                    type="text"
                                    placeholder="Search bonds..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 form-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status Filter Card - 20% width (1 out of 5 columns) */}
                    <div className="card lg:col-span-1">
                        <div className="card-body">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full form-input"
                            >
                                <option value="all">All Holdings</option>
                                <option value="active">Active Only</option>
                                <option value="inactive">Inactive Only</option>
                            </select>
                        </div>
                    </div>

                    {/* Type Filter Card - 20% width (1 out of 5 columns) */}
                    <div className="card lg:col-span-1">
                        <div className="card-body">
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="w-full form-input"
                            >
                                <option value="all">All Types</option>
                                <option value="Fixed Notes">Conventional</option>
                                <option value="Sukuk">Sukuk</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bond Holdings Table */}
                <div className="card">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-gray-900">
                                Bond Holdings ({totalBonds})
                            </h2>
                           
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Bond Details</th>
                                    <th className="table-header-cell">Status</th>
                                    <th className="table-header-cell">Token Holders</th>
                                    <th className="table-header-cell">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {pageBonds.length > 0 ? (
                                    pageBonds.map((bond) => {
                                        const resolvedBondId = bond._id || bond.bondId;
                                        const allocation = bond.allocationInfo?.totalAllocationAmount || 0;
                                        const holdersCount = bond.tokenHolders ?? bond.allocationInfo?.allocationsCount ?? 0;
                                        return (
                                            <tr key={resolvedBondId} className="table-row">
                                                <td className="table-cell">
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="font-medium text-gray-900">{bond.bondName}</div>
                                                            <span className="px-2 py-1 text-xs font-medium rounded bg-primary-100 text-primary-800">
                                                                {bond.tokenDetails?.symbol || '—'}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 text-sm text-secondary-500">
                                                            {bond.productType} • {bond.isin || 'Pending ISIN'}
                                                        </div>
                                                        {bond.bondNumber && (
                                                            <div className="mt-1 font-mono text-xs text-secondary-600">
                                                                Bond #: {bond.bondNumber}
                                                            </div>
                                                        )}
                                                        <div className="mt-1 text-xs text-dark-gray">
                                                            Issued: {formatDate(bond.createdAt)}
                                                        </div>
                                                        <div className="mt-1 text-xs font-medium text-primary-600">
                                                            Allocation: {formatCurrency(allocation)}
                                                        </div>
                                                        {bond.tokenDetails?.tokenAddress && (
                                                            <div className="text-[10px] text-dark-gray mt-1 font-mono truncate" title={bond.tokenDetails.tokenAddress}>
                                                                Token: {bond.tokenDetails.tokenAddress}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div>
                                                        {getStatusBadge(bond.status)}
                                                        {bond.submittedAt && (
                                                            <div className="mt-1 text-xs text-gray-400">
                                                                Submitted: {formatDate(bond.submittedAt)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <span className="text-sm font-medium text-secondary-900">
                                                        {holdersCount}
                                                    </span>
                                                    <div className="text-[10px] text-secondary-500 mt-0.5">Sub-investor allocations</div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={true ? () => navigate(`/custodian/bonds/${resolvedBondId}/holders`) : undefined}
                                                            
                                                            className="p-1 text-primary-600 hover:text-primary-700"
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        {(() => {
                                                            const tokenAddress = bond?.tokenDetails?.tokenAddress || bond?.tokenDetails?.contractAddress;
                                                            const explorerBase = 'https://teleskop.aeris-dev.codedsolution-web3.com/address/';
                                                            if (!tokenAddress) {
                                                                return <span className="text-xs text-gray-400" title="Token not yet created">—</span>;
                                                            }
                                                            const hasExplorerPermission = true;
                                                            return (
                                                                <a
                                                                    href={hasExplorerPermission ? `${explorerBase}${tokenAddress}` : '#'}
                                                                    target={hasExplorerPermission ? "_blank" : undefined}
                                                                    rel={hasExplorerPermission ? "noopener noreferrer" : undefined}
                                                                    onClick={!hasExplorerPermission ? (e) => e.preventDefault() : undefined}
                                                                    className={`text-primary-600 hover:text-primary-700 p-1 ${!hasExplorerPermission ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                                                                    title={!hasExplorerPermission ? 'No permission to view explorer' : 'View on Explorer'}
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </a>
                                                            );
                                                        })()}
                                                        {/* {bond.status === 'Active' && (
                                                            <button
                                                                onClick={() => navigate(`/custodian/payouts?bondId=${resolvedBondId}`)}
                                                                className="p-1 text-success-600 hover:text-success-700"
                                                                title="Manage Payouts"
                                                            >
                                                                <Coins className="w-4 h-4" />
                                                            </button>
                                                        )} */}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="table-cell py-12 text-center">
                                            <div className="text-secondary-500">
                                                <Coins className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
                                                <p className="mb-2 text-lg font-medium">
                                                    {searchTerm ? 'No allocated bonds found' : 'No bond allocations yet'}
                                                </p>
                                                <p className="mb-4 text-sm">
                                                    {searchTerm
                                                        ? `No allocated bonds match your search "${searchTerm}"`
                                                        : 'You have no bond allocations assigned to your custodian account'
                                                    }
                                                </p>                                               
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {totalBonds > 0 && (
                            <div className="flex flex-col items-center justify-between gap-4 px-4 py-4 border-t sm:flex-row">
                                <div className="text-sm text-secondary-600">
                                    Showing {totalBonds === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, totalBonds)} of {totalBonds} bonds • Page {currentPage} of {totalPages}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >Prev</button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={`px-2 py-1 rounded border text-sm mx-0.5 ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}
                                            onClick={() => setCurrentPage(page)}
                                            disabled={page === currentPage}
                                        >{page}</button>
                                    ))}
                                    <button
                                        className="px-2 py-1 text-sm border rounded disabled:opacity-50"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >Next</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustodianBondHoldings;
